import type { Grant, GrantsFilters } from "./grants.types";
import type { EligibilityStatus } from "./EligibilityBadge";

type PatientData = {
  state: string | null;
  age: number | null;
  hasFertilityCoverage: boolean | null;
};

/**
 * Check if a grant matches the patient's profile.
 * Returns null if clearly ineligible, or an EligibilityStatus.
 */
export function checkGrantEligibility(
  grant: Grant,
  patient: PatientData,
): EligibilityStatus | null {
  const { eligibility } = grant;
  let hasAllData = true;
  let hasAnyMismatch = false;

  // Location check
  if (eligibility.location !== "national") {
    if (patient.state) {
      if (!eligibility.location.includes(patient.state)) {
        hasAnyMismatch = true;
      }
    } else {
      hasAllData = false;
    }
  }

  // Age check
  if (eligibility.ageMin != null || eligibility.ageMax != null) {
    if (patient.age != null) {
      if (eligibility.ageMin != null && patient.age < eligibility.ageMin) {
        hasAnyMismatch = true;
      }
      if (eligibility.ageMax != null && patient.age > eligibility.ageMax) {
        hasAnyMismatch = true;
      }
    } else {
      hasAllData = false;
    }
  }

  // Insurance check
  if (eligibility.insuranceRequired === "no_fertility") {
    if (patient.hasFertilityCoverage != null) {
      if (patient.hasFertilityCoverage) {
        hasAnyMismatch = true;
      }
    } else {
      hasAllData = false;
    }
  }

  if (hasAnyMismatch) return null;
  if (hasAllData) return "eligible";
  return "potentially_eligible";
}

/**
 * Filter grants by search, treatments, status, and patient match.
 */
export function filterGrants(
  grants: Grant[],
  filters: GrantsFilters,
  patient?: PatientData,
): Grant[] {
  let result = grants;

  // Search
  if (filters.search?.trim()) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        g.organization.toLowerCase().includes(q) ||
        g.shortDescription.toLowerCase().includes(q),
    );
  }

  // Treatments
  if (filters.treatments?.length) {
    result = result.filter((g) =>
      filters.treatments!.some((t) => g.treatmentsCovered.includes(t)),
    );
  }

  // Application status
  if (filters.applicationStatus?.length) {
    result = result.filter((g) =>
      filters.applicationStatus!.includes(g.applicationStatus),
    );
  }

  // Matches user
  if (filters.matchesUser && patient) {
    result = result.filter((g) => checkGrantEligibility(g, patient) !== null);
  }

  return result;
}
